import { Button } from "@/components/ui/button";
import { Bell, CheckCircle } from "lucide-react";
import { formatPrice } from "@/lib/currency";

/** Max seats per booking — shared by the stepper and the ?seats= resume param. */
export const MAX_SEATS_PER_BOOKING = 5;

/** Stepper ceiling: never more than the per-booking max or the seats actually left (min 1 so the stepper still renders). */
export const getSeatCap = (remainingSeats?: number | null) =>
  remainingSeats == null
    ? MAX_SEATS_PER_BOOKING
    : Math.max(1, Math.min(MAX_SEATS_PER_BOOKING, remainingSeats));

interface BookingControlsProps {
  seats: number;
  onSeatsChange: (seats: number) => void;
  onBook: () => void;
  isProcessing: boolean;
  isFullyCoveredByCredits: boolean;
  creditsToUse: number;
  displayPrice: number;
  disabled?: boolean;
  isSoldOut: boolean;
  /** Action wording before the price — "Book & Subscribe" when joining Nimi Friends. */
  bookLabel?: string;
  onNotifyMe?: () => void;
  hasInterest?: boolean;
  isRegisteringInterest?: boolean;
  isLoadingInterest?: boolean;
  onCancelInterest?: () => void;
  isCancellingInterest?: boolean;
  currency?: string;
  /** Seats left on the event; caps the stepper. */
  remainingSeats?: number | null;
}

const BookingControls = ({
  seats,
  onSeatsChange,
  onBook,
  isProcessing,
  isFullyCoveredByCredits,
  creditsToUse,
  displayPrice,
  disabled,
  isSoldOut,
  bookLabel = "Book",
  onNotifyMe,
  hasInterest,
  isRegisteringInterest,
  isLoadingInterest,
  onCancelInterest,
  isCancellingInterest,
  currency = "gbp",
  remainingSeats,
}: BookingControlsProps) => {
  const seatCap = getSeatCap(remainingSeats);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => onSeatsChange(Math.max(1, seats - 1))}
            className="w-8 h-8 rounded-full bg-muted text-foreground font-bold"
          >
            -
          </button>
          <span className="text-foreground">{seats}</span>
          <button
            onClick={() => onSeatsChange(Math.min(seatCap, seats + 1))}
            disabled={seats >= seatCap}
            aria-label="Add seat"
            className="w-8 h-8 rounded-full bg-muted text-foreground font-bold disabled:opacity-40"
          >
            +
          </button>
        </div>
        
        {!isSoldOut && (
          <Button
            onClick={onBook}
            className="bg-secondary hover:bg-secondary/90 text-secondary-foreground px-8"
            disabled={disabled || isProcessing}
          >
            {isProcessing && "Processing..."}
            {!isProcessing && isFullyCoveredByCredits && (
              <span>Book with {creditsToUse} {creditsToUse === 1 ? 'credit' : 'credits'}</span>
            )}
            {!isProcessing && !isFullyCoveredByCredits && (
              <span>{bookLabel} · {formatPrice(displayPrice, currency)}</span>
            )}
          </Button>
        )}

        {isSoldOut && (
          <Button className="bg-destructive text-destructive-foreground px-8" disabled>
            Sold Out
          </Button>
        )}
      </div>

      {isSoldOut && onNotifyMe && !isLoadingInterest && (
        <Button
          onClick={hasInterest ? onCancelInterest : onNotifyMe}
          variant="outline"
          className="w-full"
          disabled={isRegisteringInterest || isCancellingInterest}
        >
          {hasInterest ? (
            isCancellingInterest ? (
              "Cancelling..."
            ) : (
              <>
                <CheckCircle className="h-4 w-4 mr-2" />
                You'll be notified — tap to cancel
              </>
            )
          ) : isRegisteringInterest ? (
            "Registering..."
          ) : (
            <>
              <Bell className="h-4 w-4 mr-2" />
              Notify me when spots become available
            </>
          )}
        </Button>
      )}
    </div>
  );
};

export default BookingControls;
